import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { PageHeader } from "../../layout/AppLayout";
import { Alert, ErpSelect, ErpTable, Modal, toOptions } from "../../components/erp";
import { errorText } from "../../api/client";
import { deheadingApi, DeheadingBreakupRow, ppDropdownsApi } from "./api";

const today = () => new Date().toISOString().slice(0, 10);

const BreakupModal: React.FC<{ row: DeheadingBreakupRow; trackId: string; onClose: () => void; onSaved: () => void }> = ({ row, trackId, onClose, onSaved }) => {
  const [v, setV] = useState({ HonQuantity: String(row.HonQuantity ?? ""), HLQuantity: String(row.HLQuantityKgs ?? ""), HLCount: String(row.HLCount ?? "") });
  const [err, setErr] = useState<string | null>(null); const [saving, setSaving] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setSaving(true); setErr(null);
    try {
      const r = await deheadingApi.post({ TrackID: trackId, Type: "BreakUp", ReceivalBreakUpID: row.ReceivalBreakUpID,
        DeheadingBreakUpID: row.DeheadingBreakUpID, ...v });
      if (r.Error) setErr(r.Error); else onSaved();
    } catch (ex) { setErr(errorText(ex)); } finally { setSaving(false); }
  };
  return (
    <Modal open title={`Deheading — ${row.LotNo}`} onClose={onClose} testId="deheading-breakup-modal">
      <form onSubmit={submit} data-testid="deheading-breakup-form">
        <div className="mb-3"><label className="form-label">Hon (Arrival) Weight</label>
          <input className="form-control" value={v.HonQuantity} onChange={(e) => setV((s) => ({ ...s, HonQuantity: e.target.value }))} data-testid="deheading-hon-qty" /></div>
        <div className="mb-3"><label className="form-label">HL Count</label>
          <input className="form-control" value={v.HLCount} onChange={(e) => setV((s) => ({ ...s, HLCount: e.target.value }))} data-testid="deheading-hl-count" /></div>
        <div className="mb-3"><label className="form-label">HL Quantity (Kgs)</label>
          <input className="form-control" value={v.HLQuantity} onChange={(e) => setV((s) => ({ ...s, HLQuantity: e.target.value }))} data-testid="deheading-hl-qty" /></div>
        {err && <Alert kind="danger" testId="deheading-breakup-error">{err}</Alert>}
        <button className="btn btn-primary" type="submit" disabled={saving} data-testid="deheading-breakup-submit">{saving ? "Saving..." : "Save"}</button>
      </form>
    </Modal>
  );
};

/** Port of v3/PreProcessing/de-heading-trackid.php — one PpcDeheading row per RmReceivingBreakup line under a
 * Receival TrackID; first save inserts a row for every breakup line, later saves edit shared header fields or
 * patch a single breakup line's weight/count. */
export default function DeheadingPage() {
  const [params] = useSearchParams();
  const trackId = params.get("TrackID") || "";
  const qc = useQueryClient();

  const dd = useQuery({ queryKey: ["pp", "dropdowns"], queryFn: ppDropdownsApi.get, staleTime: 5 * 60_000 });
  const detail = useQuery({ queryKey: ["pp", "deheading", trackId], enabled: !!trackId, queryFn: () => deheadingApi.get(trackId) });

  const [form, setForm] = useState({ Plant: "", Center: "", TransactionDate: today(), Shift: "", Supervisor: "", GradingPlantID: "0", Remarks: "" });
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null); const [saving, setSaving] = useState(false);
  const [editRow, setEditRow] = useState<DeheadingBreakupRow | null>(null);

  const header = detail.data?.DeheadingDetails;
  const rows = detail.data?.DeheadingBreakUp?.DeheadingData || [];
  const totals = detail.data?.DeheadingBreakUp?.Totals;
  const plants = useMemo(() => toOptions(dd.data?.Data?.Plant, (p) => p.ProcessingStationID, (p) => p.StationName), [dd.data]);
  const centers = useMemo(() => toOptions(dd.data?.Data?.Center, (c) => c.StationID, (c) => c.UnitName), [dd.data]);
  const shifts = dd.data?.Data?.Shift || []; const supervisors = dd.data?.Data?.Supervisor || [];

  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setSaving(true); setMsg(null);
    try {
      const r = await deheadingApi.post({ TrackID: trackId, Type: "", ...form });
      if (r.Error) setMsg({ ok: false, text: r.Error }); else { setMsg({ ok: true, text: "Saved." }); qc.invalidateQueries({ queryKey: ["pp", "deheading", trackId] }); }
    } catch (ex) { setMsg({ ok: false, text: errorText(ex) }); } finally { setSaving(false); }
  };

  return (
    <>
      <PageHeader title="Deheading" location="Preprocessing" />
      <div className="card mb-3"><div className="card-body">
        <form onSubmit={submit} data-testid="deheading-header-form">
          <div className="row">
            <div className="form-group col-md-3 mb-3"><label className="form-label">Plant</label>
              <ErpSelect id="deheading-plant" value={form.Plant} onChange={(v) => setForm((s) => ({ ...s, Plant: v }))} options={plants} placeholder="Select Plant" testId="deheading-plant" /></div>
            <div className="form-group col-md-3 mb-3"><label className="form-label">Center</label>
              <ErpSelect id="deheading-center" value={form.Center} onChange={(v) => setForm((s) => ({ ...s, Center: v }))} options={centers} placeholder="Select Center" testId="deheading-center" /></div>
            <div className="form-group col-md-3 mb-3"><label className="form-label">Date</label>
              <input className="form-control" type="date" value={form.TransactionDate} onChange={(e) => setForm((s) => ({ ...s, TransactionDate: e.target.value }))} data-testid="deheading-date" /></div>
            <div className="form-group col-md-3 mb-3"><label className="form-label">Shift</label>
              <select className="form-select" value={form.Shift} onChange={(e) => setForm((s) => ({ ...s, Shift: e.target.value }))} data-testid="deheading-shift">
                <option value="">Select Shift</option>{shifts.map((o) => <option key={o.OptID} value={o.OptID}>{o.Name}</option>)}
              </select></div>
            <div className="form-group col-md-3 mb-3"><label className="form-label">Supervisor</label>
              <select className="form-select" value={form.Supervisor} onChange={(e) => setForm((s) => ({ ...s, Supervisor: e.target.value }))} data-testid="deheading-supervisor">
                <option value="">Select</option>{supervisors.map((o) => <option key={o.ID} value={o.ID}>{o.UserName}</option>)}
              </select></div>
            <div className="form-group col-md-6 mb-3"><label className="form-label">Remarks</label>
              <input className="form-control" value={form.Remarks} onChange={(e) => setForm((s) => ({ ...s, Remarks: e.target.value }))} data-testid="deheading-remarks" /></div>
          </div>
          {header?.GradingPlantName && <p className="mb-2">Grading Plant: <strong data-testid="deheading-grading-plant">{header.GradingPlantName}</strong></p>}
          {msg && <Alert kind={msg.ok ? "success" : "danger"} testId="deheading-msg">{msg.text}</Alert>}
          <button className="btn btn-primary" type="submit" disabled={saving} data-testid="deheading-submit">{saving ? "Saving..." : "Save"}</button>
        </form>
      </div></div>

      <div className="card"><div className="card-body">
        <h6 className="mb-3">Deheading Breakup</h6>
        <ErpTable<DeheadingBreakupRow> testId="deheading-breakup-table" loading={detail.isLoading}
          error={detail.isError ? errorText(detail.error) : null} rows={rows}
          columns={[{ key: "LotNo", title: "Lot No" }, { key: "ArrivalWeight", title: "Arrival Wt" }, { key: "HonQuantity", title: "Hon Wt" },
            { key: "HLCount", title: "HL Count" }, { key: "HLQuantityKgs", title: "HL Qty" }, { key: "Yield", title: "Yield %" },
            { key: "Action", title: "Action", render: (r) => <button className="btn btn-sm btn-outline-primary" data-testid={`deheading-edit-${r.DeheadingBreakUpID}`} onClick={() => setEditRow(r)}>Edit</button> }]}
          footer={totals && <tr><td><strong>Totals</strong></td><td>{totals.TotalArrivalWeight}</td><td>{totals.TotalHOnQuantity}</td><td /><td>{totals.TotalHLQuantity}</td><td>{totals.AvgYield}</td><td /></tr>} />
      </div></div>
      {editRow && <BreakupModal row={editRow} trackId={trackId} onClose={() => setEditRow(null)}
        onSaved={() => { setEditRow(null); qc.invalidateQueries({ queryKey: ["pp", "deheading", trackId] }); }} />}
    </>
  );
}
